import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import classnames from 'classnames';
import Downshift from 'downshift';
import { isNil, noop } from 'lodash';

import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { Icon, FormFieldError, Label } from 'shared/components';
import { ASTERISK, OPTIONAL, REQUIRED } from 'shared/components/_constants/label';

import {
  DeleteIcon,
  LoadingMessage,
  Menu,
} from './elements';

import theme from './Select.scss';

class Select extends PureComponent {
  static propTypes = {
    name: PropTypes.string,
    testId: PropTypes.string.isRequired,
    ariaLabel: PropTypes.object,
    rootClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    menuClassName: PropTypes.string,
    selectedItem: PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      iconName: PropTypes.string,
    }),
    items: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      iconName: PropTypes.string,
    })),

    label: PropTypes.object,
    labelSuffix: PropTypes.oneOfType([
      PropTypes.oneOf([ASTERISK, OPTIONAL, REQUIRED]),
      PropTypes.node,
    ]),
    suffix: PropTypes.node,
    prefix: PropTypes.node,
    showSelectIcon: PropTypes.bool,
    placeholder: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    enableAsterisk: PropTypes.bool,
    loadingMessage: PropTypes.object,
    intl: PropTypes.object.isRequired,

    size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']),
    mediaResized: PropTypes.bool,
    status: PropTypes.oneOf([
      requestsStatuses.default,
      requestsStatuses.failure,
      requestsStatuses.pending,
      requestsStatuses.success,
    ]),
    iconName: PropTypes.string,
    disabled: PropTypes.bool,
    clearable: PropTypes.bool,
    visibleItemsAmount: PropTypes.number,

    onChange: PropTypes.func.isRequired,
    onInputClick: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    tooltipType: PropTypes.oneOf(['text', 'hint', 'intl']),
    tooltipTestId: PropTypes.string,
    tooltipMessage: PropTypes.object,
    tooltipSize: PropTypes.oneOf(['medium', 'large']),
    tooltipWidth: PropTypes.number,
    tooltipPlacement: PropTypes.string,

    overlay: PropTypes.bool,
    viewportRef: PropTypes.func,
    updateOnScroll: PropTypes.bool,
  };

  static defaultProps = {
    name: null,
    ariaLabel: null,
    rootClassName: '',
    inputClassName: '',
    menuClassName: '',
    selectedItem: null,
    items: [],
    label: null,
    labelSuffix: null,
    suffix: null,
    prefix: null,
    showSelectIcon: true,
    placeholder: { defaultMessage: ' ', id: 'default' },
    enableAsterisk: false,
    loadingMessage: null,

    size: 'small',
    mediaResized: true,
    status: requestsStatuses.default,
    iconName: '',
    disabled: false,
    clearable: true,
    visibleItemsAmount: 4,

    onInputClick: noop,
    onFocus: noop,
    onBlur: noop,

    tooltipType: undefined,
    tooltipTestId: 'tooltip',
    tooltipMessage: null,
    tooltipSize: 'large',
    tooltipWidth: null,
    tooltipPlacement: 'bottomStart',

    overlay: false,
    viewportRef: null,
    updateOnScroll: false,
  };

  static getDerivedStateFromProps(props, state) {
    const newSelectedItem = props.selectedItem;
    const currentSelectedItem = state.selectedItem;

    return newSelectedItem && newSelectedItem.value !== currentSelectedItem.value
      ? {
        selectedItem: {
          key: newSelectedItem.key,
          value: newSelectedItem.value,
        },
      }
      : !newSelectedItem && currentSelectedItem.value && currentSelectedItem.value !== ''
        ? {
          selectedItem: {
            key: null,
            value: '',
          },
        }
        : null;
  }

  constructor(props) {
    super(props);

    const { selectedItem } = props;

    this.state = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: 0,
      height: 0,
      menuMaxHeight: 0,
      menuTopOffset: 0,
      isMenuStickedToTop: false,
      selectedItem: {
        key: selectedItem ? selectedItem.key : null,
        value: selectedItem ? selectedItem.value : '',
      },
      focused: false,
    };
  }

  componentDidMount() {
    this.getInputMetrics();
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  getIconName = (selectedItem) => {
    const { iconName } = this.props;

    return (selectedItem && selectedItem.iconName) ? selectedItem.iconName : iconName;
  };

  getInputMetrics = () => {
    const input = this.input;

    if (!input) {
      return false;
    }

    const { top, bottom, left, width, height } = input.getBoundingClientRect();

    this.setState({
      left,
      width,
      height,
    });

    this.setMenuPosition({
      top,
      bottom,
      height,
    });
  };

  onStateChange = ({ isOpen }, { reset }) => {
    if (typeof isOpen !== 'boolean') return;
    if (isOpen) {
      this.getInputMetrics();
      this.addListeners();
    } else {
      reset();
      this.removeListeners();
    }
  };

  setMenuPosition = ({ top, bottom, height }) => {
    const { items, visibleItemsAmount, viewportRef } = this.props;

    const menuHeight = items.length * (height - 4);
    const menuMaxHeight = (visibleItemsAmount * (height - 4)) + 4;

    const hightNeeded = menuHeight < menuMaxHeight ? menuHeight : menuMaxHeight; // rewrite max height calculation on onChange

    let innerHeight = window.innerHeight;
    if (viewportRef) {
      const root = viewportRef();
      if (root) {
        const { bottom: rootBottom } = root.getBoundingClientRect();
        innerHeight = rootBottom;
      }
    }
    const isMenuStickedToTop = (top + hightNeeded + 10) < innerHeight;
    const menuTopOffset = !isMenuStickedToTop ? (bottom - hightNeeded - 4) : top;

    this.setState({
      menuTopOffset,
      menuMaxHeight,
      isMenuStickedToTop,
    });
  };

  addListeners = () => {
    window.addEventListener('resize', this.getInputMetrics);
    window.addEventListener('scroll', this.getInputMetrics);
  };

  removeListeners = () => {
    window.removeEventListener('resize', this.getInputMetrics);
    window.removeEventListener('scroll', this.getInputMetrics);
  };

  handleFocus = (e, callback) => {
    this.setState({ focused: true });

    if (this.input) {
      this.input.focus();
    }

    this.props.onFocus(e);
    if (callback) callback();
  };

  handleBlur = (e, callback) => {
    this.setState({ focused: false });
    this.input.blur();
    this.props.onBlur();
    if (callback) callback();
  };

  handleChange = (selection) => {
    if (!isNil(selection)) {
      this.setState({ selectedItem: selection });
    }

    this.props.onChange(selection);
    this.handleBlur();
  };

  handleInputClick = (e, openMenu) => {
    if (this.props.disabled) return;

    this.getInputMetrics();
    openMenu();
    this.props.onInputClick(e);
    this.props.onFocus(e);
  };

  handleDeleteIconClick = () => {
    this.props.onChange(null);
  };

  findHighlighted = (items, selectedItem) => {
    return items.findIndex(item => item && this.props.selectedItem && item.key === selectedItem.key);
  };

  render() {
    const {
      name,
      ariaLabel,
      testId,
      rootClassName,
      inputClassName,
      menuClassName,
      items,
      label,
      labelSuffix,
      suffix,
      prefix,
      showSelectIcon,
      placeholder,
      loadingMessage,
      intl,
      size,
      mediaResized,
      status,
      iconName,
      clearable,
      disabled,
      tooltipType,
      tooltipTestId,
      tooltipMessage,
      tooltipSize,
      tooltipWidth,
      tooltipPlacement,
      overlay,
    } = this.props;

    const {
      left,
      width,
      height,
      isMenuStickedToTop,
      menuTopOffset,
      menuMaxHeight,
      selectedItem,
      focused,
    } = this.state;

    return (
      <div className={ classnames(rootClassName, theme.root) }>
        <Downshift
          key={ items.length }
          onSelect={ this.handleChange }
          itemToString={ item => (item ? item.value : '') }
          selectedItem={ selectedItem }
          defaultHighlightedIndex={ this.findHighlighted(items, selectedItem) }
          onStateChange={ this.onStateChange }
        >
          { ({
               getInputProps,
               getItemProps,
               getLabelProps,
               getMenuProps,
               isOpen,
               openMenu,
               closeMenu,
               inputValue,
               highlightedIndex,
               selectedItem,
               clearSelection,
             }) =>
            (
              <div>
                { label && (
                  <Label
                    { ...getLabelProps() }
                    label={ label }
                    labelSuffix={ labelSuffix }
                  />
                ) }

                <div className={ classnames({
                  [theme.iconRight]: !!iconName,
                  [theme.iconLeft]: this.getIconName(selectedItem),
                  [theme.iconRtlNotReversed]: !!iconName,
                  [theme[size]]: size,
                  [theme.mediaResized]: mediaResized,
                  [theme[status.toLowerCase()]]: status.toLowerCase(),
                  [theme.disabled]: disabled,
                  [theme.inputFocused]: focused && !isOpen,
                }, theme.inputRoot, theme.iconDelete, inputClassName) }
                >

                  <div
                    className={ theme.input }
                    ref={ (ref) => this.input = ref }
                  >

                    <If condition={ loadingMessage && status === requestsStatuses.pending }>
                      <LoadingMessage
                        loadingMessage={ loadingMessage }
                        size={ size }
                        mediaResized={ mediaResized }
                      />
                    </If>

                    <If condition={ prefix }>
                      <div
                        className={ theme.prefix }
                        onClick={ (e) => this.handleInputClick(e, openMenu) }
                      >
                        { prefix }
                      </div>
                    </If>

                    { this.getIconName(selectedItem) && (
                      <div
                        className={ classnames(theme.iconContainer, {
                          [theme.fullField]: inputValue,
                          [theme.disabled]: disabled,
                        }) }
                        onClick={ (e) => this.handleInputClick(e, openMenu) }
                      >
                        <Icon name={ this.getIconName(selectedItem) } />
                      </div>
                    ) }

                    <input
                      aria-label={ ariaLabel && intl.formatMessage(ariaLabel) }
                      data-testid={ testId }
                      { ...getInputProps({
                        name,
                        placeholder: typeof (placeholder) === 'string' ? placeholder : intl.formatMessage({ ...placeholder }),
                        disabled,
                        autoComplete: 'off',
                        type: 'text',
                        readOnly: true,
                        onFocus: (e) => { this.handleFocus(e, openMenu); },
                        onBlur: (e) => { this.handleBlur(e, closeMenu); },
                        onClick: (e) => this.handleInputClick(e, openMenu),
                      }) }
                    />

                    <If condition={ clearable && inputValue && !disabled }>
                      <DeleteIcon
                        intl={ intl }
                        clearSelection={ clearSelection }
                        size={ size }
                        mediaResized={ mediaResized }
                        handleClick={ this.handleDeleteIconClick }
                      />
                    </If>

                    <If condition={ showSelectIcon }>
                      <Icon
                        name="dropdown-chevron-fill"
                        className={ classnames(theme.selectIcon, {
                          [theme[size]]: size,
                          [theme.mediaResized]: mediaResized,
                          [theme.disabled]: disabled,
                        }) }
                        onClick={ (e) => this.handleInputClick(e, openMenu) }
                      />
                    </If>

                    <If condition={ suffix }>
                      <div
                        className={ theme.suffix }
                        onClick={ (e) => this.handleInputClick(e, openMenu) }
                      >
                        { suffix }
                      </div>
                    </If>

                    { isOpen && (
                      <Menu
                        menuClassName={ menuClassName }
                        size={ size }
                        mediaResized={ mediaResized }
                        left={ left }
                        width={ width }
                        isMenuStickedToTop={ isMenuStickedToTop }
                        menuTopOffset={ menuTopOffset }
                        menuMaxHeight={ menuMaxHeight }
                        items={ items }
                        inputValue={ inputValue }
                        highlightedIndex={ highlightedIndex }
                        selectedItem={ selectedItem }
                        getItemProps={ getItemProps }
                        getMenuProps={ getMenuProps }
                        overlay={ overlay }
                      />
                    ) }
                  </div>
                </div>

                <FormFieldError
                  tooltipType={ tooltipType }
                  testId={ tooltipTestId }
                  width={ tooltipWidth }
                  offset={ height }
                  tooltipMessage={ tooltipMessage }
                  tooltipSize={ tooltipSize }
                  tooltipPlacement={ tooltipPlacement }
                />

              </div>
            ) }
        </Downshift>
      </div>
    );
  }
}

export default injectIntl(Select);
